import React, { useState, useEffect } from 'react';
import { FileText, Key, Sun, Moon } from 'lucide-react';
import ApiKeySettings from './ApiKeySettings';
import { useTheme } from './ThemeProvider';

const Header: React.FC = () => {
  const [showApiSettings, setShowApiSettings] = useState(false);
  const [hasUserKey, setHasUserKey] = useState(false);
  const { resolvedTheme, setTheme } = useTheme();

  // Other parts of the app (the Premiere extension card) and links to /#api-keys
  // open the settings, where users make the API key the panel needs.
  useEffect(() => {
    const open = () => setShowApiSettings(true);
    window.addEventListener('open-api-settings', open);
    if (window.location.hash === '#api-keys') open();
    return () => window.removeEventListener('open-api-settings', open);
  }, []);

  useEffect(() => {
    // Check if user has saved an API key
    const savedKey = localStorage.getItem('user_google_api_key');
    setHasUserKey(!!savedKey);
  }, []);

  return (
    <>
      <header className="glass-nav transition-all duration-300 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 max-w-5xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 group cursor-pointer">
              <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg shadow-indigo-500/20 transform group-hover:scale-105 transition-all duration-300">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 tracking-tight">TRB's Subtitle Studio</h1>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium tracking-wide">Advanced Audio Transcription</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                className="p-2 text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-all duration-200"
                title={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {resolvedTheme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>

              <button
                type="button"
                onClick={() => setShowApiSettings(true)}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-200 
                         bg-white/50 dark:bg-slate-800/50 border border-slate-200/50 dark:border-slate-700/50
                         hover:bg-indigo-50 dark:hover:bg-slate-700/80 hover:text-indigo-600 dark:hover:text-indigo-400 
                         hover:border-indigo-200 dark:hover:border-indigo-500/30
                         rounded-xl transition-all duration-300 relative shadow-sm hover:shadow-md"
                title="API Key Settings"
              >
                <Key size={18} />
                <span className="hidden sm:inline">API Settings</span>
                {hasUserKey && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full border-2 border-white dark:border-slate-900"></span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {showApiSettings && (
        <ApiKeySettings onClose={() => {
          setShowApiSettings(false);
          // Re-check if user has saved key after closing
          const savedKey = localStorage.getItem('user_google_api_key');
          setHasUserKey(!!savedKey);
        }} />
      )}
    </>
  );
};

export default Header;